import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AlertOctagon } from 'lucide-react';
import { usePayment } from '../contexts/PaymentContext';

const PaymentFail = () => {
  const { resetPaymentState } = usePayment();
  const location = useLocation();
  
  // Reset payment state when component mounts
  useEffect(() => {
    resetPaymentState();
    
    // Clean up any pending payment data
    localStorage.removeItem('pendingPaymentId');
    localStorage.removeItem('pendingSubscriptionId');
    localStorage.removeItem('pendingPaymentData');
    localStorage.removeItem('pendingSubscriptionData');
    
    // Log the failure reason if available in URL
    const queryParams = new URLSearchParams(location.search);
    const errorCode = queryParams.get('error_code');
    const errorMessage = queryParams.get('error_message');
    
    if (errorCode || errorMessage) {
      console.error('Payment failed:', { errorCode, errorMessage });
    }
  }, [resetPaymentState, location.search]);
  
  return (
    <div className="max-w-3xl mx-auto px-4 py-16 text-center">
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 md:p-12">
        <div className="flex justify-center mb-6">
          <AlertOctagon size={80} className="text-red-500" />
        </div>
        
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Payment Failed</h1>
        
        <p className="text-gray-600 mb-8 text-lg">
          We were unable to process your payment. Please review your payment details and try again.
        </p>
        
        <div className="bg-gray-50 rounded-lg p-6 mb-8 text-left">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Common reasons for failure:</h2>
          
          <ul className="space-y-3">
            <li className="flex items-start">
              <div className="h-6 w-6 rounded-full bg-red-100 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                <span className="text-red-600">•</span>
              </div>
              <span className="text-gray-700">Insufficient funds in your account</span>
            </li>
            <li className="flex items-start">
              <div className="h-6 w-6 rounded-full bg-red-100 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                <span className="text-red-600">•</span>
              </div>
              <span className="text-gray-700">Card verification failed</span>
            </li>
            <li className="flex items-start">
              <div className="h-6 w-6 rounded-full bg-red-100 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                <span className="text-red-600">•</span>
              </div>
              <span className="text-gray-700">The card has been declined by your bank</span>
            </li>
            <li className="flex items-start">
              <div className="h-6 w-6 rounded-full bg-red-100 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                <span className="text-red-600">•</span>
              </div>
              <span className="text-gray-700">Incorrect card information provided</span>
            </li>
          </ul>
        </div>
        
        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <Link 
            to="/checkout" 
            className="px-8 py-3 rounded-full font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors duration-200 shadow-md"
          >
            Try Again
          </Link>
          
          <Link 
            to="/support" 
            className="px-8 py-3 rounded-full font-medium bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 transition-colors duration-200"
          >
            Contact Support
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PaymentFail; 